import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth, hasRole } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar, Plus, ChevronRight, Loader2, Play } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";

export default function Cycles() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const isAdmin = hasRole(user, "tenant_admin", "hr_admin");

  const load = () => {
    setLoading(true);
    api.get("/cycles").then((r) => setItems(r.data.items || [])).finally(() => setLoading(false));
  };
  useEffect(load, []);

  return (
    <div className="max-w-7xl space-y-6" data-testid="cycles-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="axia-overline">Performance</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mt-1.5">Cycles</h1>
          <p className="text-sm text-slate-500 mt-2 max-w-xl">Annual, half-yearly, or quarterly review cycles. Activation auto-routes reviewers along the org chart.</p>
        </div>
        {isAdmin && (
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button data-testid="cycle-create-btn"><Plus className="w-4 h-4 mr-2" /> New cycle</Button>
            </DialogTrigger>
            <CreateCycleDialog onClose={() => { setCreateOpen(false); load(); }} />
          </Dialog>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="axia-card p-12 text-center text-slate-500">
          <Calendar className="w-6 h-6 mx-auto text-slate-300" />
          <div className="font-display text-lg font-semibold text-slate-900 mt-3">No cycles yet</div>
          <p className="text-sm mt-1">Create your first cycle from a published framework.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((c) => (
            <Link
              key={c.id}
              to={`/cycles/${c.id}`}
              className="block axia-card p-5 hover:border-slate-400 transition"
              data-testid={`cycle-row-${c.id}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-display text-base font-semibold text-slate-900">{c.name}</h3>
                    <span
                      className={`axia-pill ${
                        c.status === "active"
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : c.status === "draft"
                          ? "bg-amber-50 text-amber-700 border-amber-200"
                          : "bg-slate-100 text-slate-600 border-slate-200"
                      }`}
                    >
                      {c.status}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {c.year} · {c.framework_snapshot?.name || "—"} · {c.phases?.length || 0} phases
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </div>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {(c.phases || []).map((p) => (
                  <span
                    key={p.id}
                    className={`axia-pill ${
                      p.status === "open"
                        ? "bg-blue-50 text-blue-700 border-blue-200"
                        : p.status === "closed"
                        ? "bg-slate-100 text-slate-600 border-slate-200"
                        : "bg-white text-slate-500 border-slate-200"
                    }`}
                  >
                    {p.label}
                  </span>
                ))}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function CreateCycleDialog({ onClose }) {
  const [frameworks, setFrameworks] = useState([]);
  const [name, setName] = useState("");
  const [year, setYear] = useState(new Date().getFullYear());
  const [frameworkId, setFrameworkId] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/frameworks").then((r) => setFrameworks((r.data.items || []).filter((f) => f.status === "published")));
  }, []);

  const submit = async () => {
    if (!name || !frameworkId) {
      toast.error("Name and framework are required");
      return;
    }
    setLoading(true);
    try {
      await api.post("/cycles", { name, year: Number(year), framework_id: frameworkId });
      toast.success("Cycle created · activate it from the cycle detail page");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create");
    } finally {
      setLoading(false);
    }
  };

  return (
    <DialogContent data-testid="create-cycle-dialog">
      <DialogHeader>
        <DialogTitle>New performance cycle</DialogTitle>
        <DialogDescription>Pick a published framework. Phases auto-roll from the framework's defaults.</DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        <div>
          <Label>Cycle name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="CY2026 — Performance Cycle" data-testid="cycle-name-input" />
        </div>
        <div>
          <Label>Year</Label>
          <Input type="number" value={year} onChange={(e) => setYear(e.target.value)} data-testid="cycle-year-input" />
        </div>
        <div>
          <Label>Framework</Label>
          <Select value={frameworkId} onValueChange={setFrameworkId}>
            <SelectTrigger data-testid="cycle-framework-select"><SelectValue placeholder="Pick a published framework" /></SelectTrigger>
            <SelectContent>
              {frameworks.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <DialogFooter>
        <Button onClick={submit} disabled={loading} data-testid="cycle-create-submit">
          {loading && <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />} Create cycle
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
